import React, { useEffect, useState } from 'react';

import PendencyModal from 'design-system/PendencyModal';
import {
  ConsultantDataReponse,
  getConsultants,
} from 'services/consultantsServices';
import { buildMailtoHref, buildWhatsAppHref } from 'utils/buildContactLinks';
import phoneMask from 'utils/phoneMask';

import Skeleton, { SkeletonRow, SkeletonStack } from 'design-system/Skeleton';
import { PENDENCIAS_TEXTS } from '../constants';

import CallCenterContent from './CallCenterContent';
import {
  ConsultantAvatar,
  ConsultantContact,
  ConsultantName,
  ConsultantRow,
  ContactLine,
  PhoneIconLink,
} from './styles';

type ConsultantModalProps = {
  open: boolean;
  onClose: () => void;
};

const WhatsAppIcon = (): JSX.Element => (
  <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
    <path d="M8 1a7 7 0 0 0-6 10.6L1 15l3.5-1A7 7 0 1 0 8 1zm0 12.6a5.6 5.6 0 0 1-2.9-.8l-.2-.1-2 .5.5-2-.1-.2A5.6 5.6 0 1 1 8 13.6zm3.2-4.2c-.2-.1-1-.5-1.2-.6-.2-.1-.3-.1-.4.1l-.5.6c-.1.1-.2.1-.4 0a4.6 4.6 0 0 1-2.2-1.9c-.2-.3.2-.3.5-1 0-.1 0-.2 0-.3l-.6-1.3c-.1-.3-.3-.3-.4-.3h-.4c-.1 0-.3.1-.5.3-.6.7-.5 1.6 0 2.4a7.4 7.4 0 0 0 3.3 3c1.2.5 1.7.5 2.3.4.4 0 1-.4 1.2-.9.1-.3.1-.6.1-.7-.1-.1-.2-.1-.4-.2z" />
  </svg>
);

const MailIcon = (): JSX.Element => (
  <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <rect
      x="1.5"
      y="3.5"
      width="13"
      height="9"
      rx="1.5"
      stroke="currentColor"
      strokeWidth="1.3"
    />
    <path
      d="m2 4.5 6 4 6-4"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinejoin="round"
    />
  </svg>
);

const fixPhoto = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) return '';

  const marker = trimmed.indexOf('base64,');
  if (marker !== -1) {
    return (
      trimmed.slice(0, marker + 7) +
      trimmed.slice(marker + 7).replace(/\s/g, '')
    );
  }

  return trimmed.replace(/\s/g, '');
};

const ConsultantModal = ({
  open,
  onClose,
}: ConsultantModalProps): JSX.Element => {
  const [consultant, setConsultant] = useState<ConsultantDataReponse | null>(
    null
  );
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!open || loaded) return;

    const load = async (): Promise<void> => {
      const response = await getConsultants();
      setConsultant(response?.[0] ?? null);
      setLoaded(true);
    };

    load();
  }, [open, loaded]);

  const photo = consultant?.photo ? fixPhoto(consultant.photo) : '';

  return (
    <PendencyModal
      open={open}
      title={PENDENCIAS_TEXTS.MODAL_CONSULTANT_TITLE}
      onClose={onClose}
      width={520}
    >
      {!loaded && (
        <SkeletonRow>
          <Skeleton
            circle
            width={96}
            height={96}
            label="Carregando os dados do consultor"
          />
          <SkeletonStack>
            <Skeleton height={20} width="45%" />
            <Skeleton height={24} width="60%" />
            <Skeleton height={24} width="80%" />
          </SkeletonStack>
        </SkeletonRow>
      )}
      {loaded && !consultant && <CallCenterContent />}
      {consultant && (
        <ConsultantRow>
          <ConsultantAvatar>
            {photo && <img src={photo} alt="" />}
          </ConsultantAvatar>

          <ConsultantContact>
            <ConsultantName>
              {consultant.consultantName || consultant.name}
            </ConsultantName>
            {consultant.phone && (
              <ContactLine>
                <a href={`tel:${consultant.phone.replace(/\D/g, '')}`}>
                  {phoneMask(consultant.phone)}
                </a>
                <PhoneIconLink
                  href={buildWhatsAppHref(consultant.phone)}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Abrir conversa no WhatsApp"
                >
                  <WhatsAppIcon />
                </PhoneIconLink>
              </ContactLine>
            )}

            {consultant.email && (
              <ContactLine>
                <span>{consultant.email}</span>
                <PhoneIconLink
                  href={buildMailtoHref(consultant.email)}
                  aria-label={`Enviar e-mail para ${consultant.email}`}
                >
                  <MailIcon />
                </PhoneIconLink>
              </ContactLine>
            )}
          </ConsultantContact>
        </ConsultantRow>
      )}
    </PendencyModal>
  );
};

export default ConsultantModal;
